import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useAuth } from '@shared/components/AuthProvider';
import { useVerifyAccountEmailMutation } from '@graphql/mutations/user.graphql.generated';
import { useEmailVerificationGate } from '@shared/hooks/useEmailVerificationGate';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { noop } from '@shared/utils/noop';
import { withWindow } from '@shared/utils/withWindow';
import { config } from '@static/js/env.config';
import { EmailVerificationStatusDocument } from './EmailVerificationStatus.graphql.generated';
import type { EmailVerificationStatusQuery } from './EmailVerificationStatus.graphql.generated';
import type { EmailVerificationDialogProps, EmailVerificationStep } from './EmailVerificationDialog.types';

type EnsureEmailVerified = () => Promise<boolean>;

// Return the user to a canonical /account/* URL on the auth origin after Auth0
// verifies their email. Passing the raw publish-page URL (e.g. the guest site)
// is not an allowlisted Auth0 callback and bounces the user to the login page.
const VERIFY_EMAIL_RETURN_URL = config.clientUri + '/account/personal-info?dialog=verifyEmailSuccess';

// Cooldown (seconds) after a send before "Resend" is allowed again, so users can't
// flood identity_service / Auth0 by hammering the button.
const RESEND_COOLDOWN_SECONDS = 30;

// After "I have verified my email", wait briefly before the live read so Auth0 has time
// to propagate the freshly-verified status. If it comes back unverified, the dialog lets
// the user retry, so a short delay is enough — we no longer need a long upfront window.
const VERIFY_PROPAGATION_DELAY_MS = 5000;

/**
 * Gates an action (e.g. website publish) behind email verification.
 *
 * Call `ensureEmailVerified()` before the gated action: it resolves `true`
 * immediately when the email is already verified, otherwise it opens the
 * verification dialog and resolves `true` once the user confirms a verified
 * status, or `false` if they dismiss it. Render `<EmailVerificationDialog {...dialogProps} />`
 * somewhere in the consuming tree.
 */
export const useEmailVerificationDialog = () => {
  const { currentUser, identifyCurrentUser } = useAuth();
  const { isEmailVerified } = useEmailVerificationGate();
  const [isPublishEmailVerificationEnabled] = useIsFeatureEnabled('publishEmailVerificationEnabled');
  const email = currentUser.profile?.email ?? undefined;

  const client = useApolloClient();
  const [sendVerifyEmail, { loading: isSending }] = useVerifyAccountEmailMutation();

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<EmailVerificationStep>('intro');
  const [isChecking, setIsChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Resolves the promise returned by the active ensureEmailVerified() call.
  const resolveRef = useRef<(verified: boolean) => void>(noop);
  // Guards against a second send firing in the async gap before the button disables.
  const isSendingRef = useRef(false);
  // Mirrors resendCooldown so the resend guard can read it without re-creating handleResend each tick.
  const resendCooldownRef = useRef(0);
  // Tracks the "verifying" propagation delay so it can be cancelled if the dialog closes/unmounts.
  const verifyTimeoutRef = useRef<number | null>(null);
  // Monotonic per-attempt token. Each confirm bumps it; close/unmount bumps it too, so a stale
  // in-flight attempt sees a changed token and bails instead of settling a later dialog session.
  const verifyAttemptRef = useRef(0);

  // Cancel any pending verify delay when the hook unmounts.
  useEffect(() => {
    return () => {
      // Intentional ref mutation on unmount: invalidate any in-flight attempt so it can't settle.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      verifyAttemptRef.current++;
      withWindow(global => {
        if (verifyTimeoutRef.current) {
          global.clearTimeout(verifyTimeoutRef.current);
        }
      });
    };
  }, []);

  // Keep the ref in sync so the resend guard reads the latest cooldown without a
  // dependency, then tick the cooldown down to zero one second at a time.
  useEffect(() => {
    resendCooldownRef.current = resendCooldown;
    if (resendCooldown <= 0) {
      return undefined;
    }
    const timeoutId = withWindow<number | null>(global => global.setTimeout(() => setResendCooldown(seconds => seconds - 1), 1000), null);
    return () =>
      withWindow(global => {
        if (timeoutId) {
          global.clearTimeout(timeoutId);
        }
      });
  }, [resendCooldown]);

  const settle = useCallback((verified: boolean) => {
    setIsOpen(false);
    resolveRef.current(verified);
    resolveRef.current = noop;
  }, []);

  const ensureEmailVerified = useCallback<EnsureEmailVerified>(() => {
    // When the feature flag is off, skip the gate entirely so publishing proceeds as before.
    if (!isPublishEmailVerificationEnabled || isEmailVerified) {
      return Promise.resolve(true);
    }
    setStep('intro');
    setCheckFailed(false);
    setSendFailed(false);
    setResendCooldown(0);
    setIsChecking(false);
    setIsOpen(true);
    return new Promise<boolean>(resolve => {
      resolveRef.current = resolve;
    });
  }, [isEmailVerified, isPublishEmailVerificationEnabled]);

  // Sends the verification email exactly once per in-flight request. A concurrent
  // call (e.g. a rapid double-click before the button disables) is collapsed into a no-op
  // so identity_service / Auth0 only ever receives a single send. Returns true only when this
  // call actually sent, so duplicate callers don't mistake the no-op for a successful send.
  const sendEmail = useCallback(async (): Promise<boolean> => {
    if (isSendingRef.current) {
      return false;
    }
    isSendingRef.current = true;
    try {
      await sendVerifyEmail({ variables: { payload: { returnUrl: VERIFY_EMAIL_RETURN_URL } } });
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      return true;
    } finally {
      isSendingRef.current = false;
    }
  }, [sendVerifyEmail]);

  const handleVerify = useCallback(async () => {
    setCheckFailed(false);
    setSendFailed(false);
    try {
      // Only advance on a real send; a de-duped duplicate leaves the in-flight call to govern the step.
      const sent = await sendEmail();
      if (sent) {
        setStep('sent');
      }
    } catch {
      // Stay on the intro step and surface the failure so the user can retry.
      setSendFailed(true);
    }
  }, [sendEmail]);

  const handleResend = useCallback(async () => {
    // Ignore resends while the cooldown is active.
    if (resendCooldownRef.current > 0) {
      return;
    }
    setCheckFailed(false);
    setSendFailed(false);
    try {
      await sendEmail();
    } catch {
      setSendFailed(true);
    }
  }, [sendEmail]);

  const handleConfirmVerified = useCallback(async () => {
    const attempt = ++verifyAttemptRef.current;
    setCheckFailed(false);
    setIsChecking(true);

    // Brief "verifying" pause so Auth0 can propagate the freshly-verified status before
    // we spend the single (throttled) confirmVerification live read.
    await new Promise<void>(resolve => {
      verifyTimeoutRef.current = withWindow<number | null>(global => global.setTimeout(resolve, VERIFY_PROPAGATION_DELAY_MS), null);
    });
    if (attempt !== verifyAttemptRef.current) {
      return;
    }

    try {
      // Re-fetch verification status fresh from the server. confirmVerification (in the query)
      // bypasses the BE's 60s email-verification cache and reads Auth0 live, and no-cache
      // bypasses the Apollo client cache.
      const { data } = await client.query<EmailVerificationStatusQuery>({
        query: EmailVerificationStatusDocument,
        fetchPolicy: 'no-cache',
      });
      if (attempt !== verifyAttemptRef.current) {
        return;
      }
      const verified = data?.me?.emailIsVerified ?? false;
      if (verified) {
        identifyCurrentUser();
        settle(true);
      } else {
        setCheckFailed(true);
      }
    } catch {
      setCheckFailed(true);
    } finally {
      if (attempt === verifyAttemptRef.current) {
        setIsChecking(false);
      }
    }
  }, [client, identifyCurrentUser, settle]);

  const handleClose = useCallback(() => {
    // Invalidate any in-flight verify attempt so a late resolution can't settle this session.
    verifyAttemptRef.current++;
    withWindow(global => {
      if (verifyTimeoutRef.current) {
        global.clearTimeout(verifyTimeoutRef.current);
      }
    });
    setIsChecking(false);
    settle(false);
  }, [settle]);

  const dialogProps = useMemo<EmailVerificationDialogProps>(
    () => ({
      isOpen,
      step,
      email,
      isSending,
      isChecking,
      checkFailed,
      sendFailed,
      resendCooldown,
      onVerify: handleVerify,
      onResend: handleResend,
      onConfirmVerified: handleConfirmVerified,
      onClose: handleClose,
    }),
    [isOpen, step, email, isSending, isChecking, checkFailed, sendFailed, resendCooldown, handleVerify, handleResend, handleConfirmVerified, handleClose],
  );

  return {
    ensureEmailVerified,
    dialogProps,
  };
};
